<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'About Me'
  },
  description: {
    type: String,
    default: 'Share your story and teaching philosophy with potential students'
  },
  showShadow: {
    type: Boolean,
    default: true
  },
  onSave: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'save'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const updateField = (field, value) => {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
  // Clear error for this field when user starts typing
  if (localErrors.value[field]) {
    delete localErrors.value[field]
  }
}

const validate = () => {
  const errors = {}

  if (!props.modelValue.highlight?.trim()) {
    errors.highlight = 'Headline is required'
  } else if (props.modelValue.highlight.length > 150) {
    errors.highlight = 'Headline must not exceed 150 characters'
  }

  if (!props.modelValue.about_me?.trim()) {
    errors.about_me = 'About me section is required'
  } else if (props.modelValue.about_me.length < 50) {
    errors.about_me = 'Please write at least 50 characters about yourself'
  } else if (props.modelValue.about_me.length > 2000) {
    errors.about_me = 'About me must not exceed 2000 characters'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

const handleSave = () => {
  if (validate()) {
    if (props.onSave) {
      props.onSave(props.modelValue)
    }
    emit('save', props.modelValue)
  }
}

defineExpose({ validate, handleSave })
</script>

<template>
  <div id="about-me" :class="['bg-white rounded-2xl p-10 mb-8', showShadow ? 'shadow-lg' : '']">
    <div class="flex items-center justify-between mb-8">
      <div>
        <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">{{ title }}</h2>
        <p class="hidden md:block text-gray-600">{{ description }}</p>
      </div>
      <div class="flex items-center space-x-2 text-sm text-gray-500">
        <span class="material-symbols-outlined">visibility</span>
        <span class="hidden md:block">Visible to students</span>
      </div>
    </div>

    <div class="space-y-8">
      <!-- Headline/Highlight Field -->
      <div id="highlight-field">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          Headline <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <span class="material-symbols-outlined absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400">stars</span>
          <input
            :value="modelValue.highlight"
            @input="updateField('highlight', $event.target.value)"
            type="text"
            required
            maxlength="150"
            placeholder="e.g., Experienced Maths Tutor - A-Level & GCSE Specialist"
            class="w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900"
          />
        </div>
        <div class="flex justify-between items-center mt-2">
          <p v-if="displayErrors.highlight" class="text-red-500 text-sm">{{ displayErrors.highlight }}</p>
          <p class="text-xs text-gray-500 ml-auto">{{ modelValue.highlight?.length || 0 }}/150 characters</p>
        </div>
        <p class="text-xs text-gray-500 mt-1">A catchy headline that summarizes your expertise</p>
      </div>

      <!-- About Me Field -->
      <div id="about-me-field">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          About Me <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <span class="material-symbols-outlined absolute left-5 top-5 text-gray-400">person</span>
          <textarea
            :value="modelValue.about_me"
            @input="updateField('about_me', $event.target.value)"
            required
            maxlength="2000"
            placeholder="Tell students about your teaching background, qualifications, teaching style, and what makes you a great tutor. Share your passion for teaching and how you help students achieve their goals."
            rows="8"
            class="w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 resize-none"
          ></textarea>
        </div>
        <div class="flex justify-between items-center mt-2">
          <p v-if="displayErrors.about_me" class="text-red-500 text-sm">{{ displayErrors.about_me }}</p>
          <p class="text-xs text-gray-500 ml-auto">{{ modelValue.about_me?.length || 0 }}/2000 characters</p>
        </div>
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
          <h4 class="font-semibold text-gray-800 mb-2 text-sm">💡 Tips for a great profile:</h4>
          <ul class="text-xs text-gray-700 space-y-1 list-disc list-inside">
            <li>Highlight your qualifications and teaching experience</li>
            <li>Describe your teaching philosophy and methods</li>
            <li>Mention any specializations or unique strengths</li>
            <li>Share what students can expect from your lessons</li>
            <li>Keep it professional yet personable</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
